import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../components/crm/constants';
import StatsPanel from '../components/crm/StatsPanel';
import KanbanBoard from '../components/crm/KanbanBoard';
import LeadsTable from '../components/crm/LeadsTable';
import EmergencyPanel from '../components/crm/EmergencyPanel';
import UsersPanel from '../components/crm/UsersPanel';
import SettingsPanel from '../components/crm/SettingsPanel';
import LeadDetailModal from '../components/crm/LeadDetailModal';
import { LayoutDashboard, KanbanSquare, Siren, ListFilter, Users, Settings, LogOut } from 'lucide-react';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'pipeline', label: 'Pipeline', icon: KanbanSquare },
  { id: 'emergency', label: 'Emergencies', icon: Siren },
  { id: 'leads', label: 'All Leads', icon: ListFilter },
  { id: 'users', label: 'Users', icon: Users, adminOnly: true },
  { id: 'settings', label: 'Settings', icon: Settings, adminOnly: true },
];

export default function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [emergencies, setEmergencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({});
  const [openLeadId, setOpenLeadId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.append(k, v); });
    const qs = p.toString();
    return qs ? `?${qs}` : '';
  }, [filters]);

  const refresh = useCallback(async () => {
    const [statsRes, leadsRes, emRes] = await Promise.all([
      api.get('/dashboard/stats'),
      api.get(`/leads${buildQuery()}`),
      api.get('/emergency'),
    ]);
    if (statsRes.status === 401) { navigate('/admin'); return; }
    if (statsRes.ok) setStats(await statsRes.json());
    if (leadsRes.ok) setLeads(await leadsRes.json());
    if (emRes.ok) setEmergencies(await emRes.json());
  }, [buildQuery, navigate]);

  useEffect(() => {
    (async () => {
      const meRes = await api.get('/auth/me');
      if (!meRes.ok) { navigate('/admin'); return; }
      const meData = await meRes.json();
      setMe(meData);
      if (meData.role === 'admin') {
        const uRes = await api.get('/users');
        if (uRes.ok) setUsers(await uRes.json());
      }
      setLoading(false);
    })();
  }, [navigate]);

  useEffect(() => { if (me) refresh(); }, [me, refresh]);

  const handleLogout = async () => {
    await api.post('/auth/logout', {});
    navigate('/admin');
  };

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400">Loading CRM...</div>;

  const visibleTabs = TABS.filter(t => !t.adminOnly || me?.role === 'admin');

  return (
    <div className="min-h-screen bg-slate-900">
      <header className="bg-slate-800 border-b border-slate-700 px-4 sm:px-6 py-3 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-lg font-bold text-white">Miami Multi-Trade CRM</h1>
            <p className="text-slate-400 text-xs">{me?.name} · {me?.role}</p>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-2 border border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white text-sm px-3 py-1.5 rounded">
            <LogOut className="w-4 h-4" />Logout
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-slate-800/60 border-b border-slate-700 px-4 sm:px-6 sticky top-[57px] z-30">
        <div className="max-w-[1600px] mx-auto flex gap-1 overflow-x-auto">
          {visibleTabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`crm-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? 'border-orange-500 text-white' : 'border-transparent text-slate-400 hover:text-white'}`}>
              <t.icon className="w-4 h-4" />{t.label}
              {t.id === 'emergency' && emergencies.filter(e => !['completed', 'closed'].includes(e.status)).length > 0 &&
                <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5">{emergencies.filter(e => !['completed', 'closed'].includes(e.status)).length}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6">
        {tab === 'dashboard' && <StatsPanel stats={stats} />}
        {tab === 'pipeline' && <KanbanBoard leads={leads} onOpen={setOpenLeadId} onChanged={refresh} />}
        {tab === 'emergency' && <EmergencyPanel emergencies={emergencies} onOpen={setOpenLeadId} />}
        {tab === 'leads' && <LeadsTable leads={leads} users={users} filters={filters} setFilters={setFilters} onOpen={setOpenLeadId} />}
        {tab === 'users' && me?.role === 'admin' && <UsersPanel me={me} />}
        {tab === 'settings' && me?.role === 'admin' && <SettingsPanel />}
      </div>

      {openLeadId && (
        <LeadDetailModal leadId={openLeadId} users={users} onClose={() => setOpenLeadId(null)} onChanged={refresh} />
      )}
    </div>
  );
}
